import * as THREE from 'three';
import { randRange } from '../util/math';

// One pooled additive Points field for all particle FX (§9): pickup pops, the
// dust trail, and finish-line confetti. Dead particles fade to black (invisible
// under additive blending), keeping it allocation-free per frame.

const POOL = 800;

export class Particles {
  readonly points: THREE.Points;
  private positions: Float32Array;
  private colors: Float32Array;
  private vel: Float32Array;
  private life: Float32Array;
  private maxLife: Float32Array;
  private base: Float32Array;
  private gravity: Float32Array;
  private cursor = 0;
  private geom: THREE.BufferGeometry;

  constructor() {
    this.positions = new Float32Array(POOL * 3);
    this.colors = new Float32Array(POOL * 3);
    this.vel = new Float32Array(POOL * 3);
    this.base = new Float32Array(POOL * 3);
    this.life = new Float32Array(POOL);
    this.maxLife = new Float32Array(POOL);
    this.gravity = new Float32Array(POOL);

    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    const mat = new THREE.PointsMaterial({
      size: 0.35,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });
    this.points = new THREE.Points(this.geom, mat);
    this.points.frustumCulled = false;
  }

  private emit(
    x: number,
    y: number,
    z: number,
    color: THREE.Color,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    gravity: number,
  ) {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % POOL;
    const i3 = i * 3;
    this.positions[i3] = x;
    this.positions[i3 + 1] = y;
    this.positions[i3 + 2] = z;
    this.vel[i3] = vx;
    this.vel[i3 + 1] = vy;
    this.vel[i3 + 2] = vz;
    this.base[i3] = color.r;
    this.base[i3 + 1] = color.g;
    this.base[i3 + 2] = color.b;
    this.life[i] = life;
    this.maxLife[i] = life;
    this.gravity[i] = gravity;
  }

  burst(pos: THREE.Vector3, color: THREE.Color, count: number, speed = 4, life = 0.6): void {
    for (let n = 0; n < count; n++) {
      const dir = new THREE.Vector3(
        randRange(-1, 1),
        randRange(0, 1.2),
        randRange(-1, 1),
      ).normalize();
      const s = randRange(speed * 0.4, speed);
      this.emit(pos.x, pos.y, pos.z, color, dir.x * s, dir.y * s, dir.z * s, life, 6);
    }
  }

  confetti(pos: THREE.Vector3, count: number): void {
    const palette = [0xff5d5d, 0xffd93d, 0x6dd47e, 0x5db4ff, 0xc56bff, 0xff9a3d];
    for (let n = 0; n < count; n++) {
      const c = new THREE.Color(palette[(Math.random() * palette.length) | 0]);
      const dir = new THREE.Vector3(randRange(-1, 1), randRange(0.5, 1.5), randRange(-1, 1)).normalize();
      const s = randRange(6, 14);
      this.emit(pos.x, pos.y + 2, pos.z, c, dir.x * s, dir.y * s, dir.z * s, randRange(1.2, 2.2), 9);
    }
  }

  trail(pos: THREE.Vector3, color: THREE.Color, strength: number): void {
    this.emit(
      pos.x + randRange(-0.3, 0.3),
      pos.y,
      pos.z + randRange(-0.3, 0.3),
      color,
      randRange(-1, 1),
      randRange(0.5, 1.5) * strength,
      randRange(-1, 1),
      0.5,
      1.5,
    );
  }

  update(dt: number): void {
    for (let i = 0; i < POOL; i++) {
      if (this.life[i] <= 0) {
        this.colors[i * 3] = 0;
        this.colors[i * 3 + 1] = 0;
        this.colors[i * 3 + 2] = 0;
        continue;
      }
      this.life[i] -= dt;
      const i3 = i * 3;
      this.vel[i3 + 1] -= this.gravity[i] * dt;
      this.positions[i3] += this.vel[i3] * dt;
      this.positions[i3 + 1] += this.vel[i3 + 1] * dt;
      this.positions[i3 + 2] += this.vel[i3 + 2] * dt;
      const f = Math.max(0, this.life[i] / this.maxLife[i]);
      this.colors[i3] = this.base[i3] * f;
      this.colors[i3 + 1] = this.base[i3 + 1] * f;
      this.colors[i3 + 2] = this.base[i3 + 2] * f;
    }
    (this.geom.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
  }
}
